import React, { FunctionComponent, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Dimensions, View, ViewStyle } from 'react-native'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { PROGRAM_MEMBERSHIP_TYPES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { CustomerName } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customer'
import {
  computeRemainderSessions,
  isUnlimitedSessionLimit,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { useLyraIntl } from '@lyrahealth-inc/shared-app-logic/src/features/intl/hooks/useLyraIntl'
import { getLegalNoticeLink } from '@lyrahealth-inc/shared-app-logic/src/features/intl/utils/getLegalNoticeLink'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import {
  FAQ_EMPLOYEE_COST_MESSAGES,
  FAQ_EMPLOYEE_MESSAGES,
  FAQ_EMPLOYEE_MESSAGES_BEFORE_LAUNCH,
  FAQ_LYRA_SELECT_MESSAGES,
  FAQ_MEMBER_MESSAGES,
  FAQ_MEMBER_MESSAGES_BEFORE_LAUNCH,
  FAQ_MESSAGES_DEFAULT,
  FAQ_QUESTIONS,
} from './messages'
import {
  LyraSelectFooterDesktop,
  LyraSelectFooterDesktopPlus,
  LyraSelectFooterLaptop,
  LyraSelectFooterMobile,
  LyraSelectFooterMobilePlus,
  LyraSelectFooterTablet,
  SoftSunriseMobileBG,
  SoftSunriseWithDogBGBody,
  SoftSunriseWithDogBGFooter,
} from '../../../assets/microsite'
import { BodyText } from '../../../atoms/bodyText/BodyText'
import { Display } from '../../../atoms/display/Display'
import { Link } from '../../../atoms/link/Link'
import { PrimaryButton } from '../../../atoms/primaryButton/PrimaryButton'
import { Title } from '../../../atoms/title/Title'
import { AppContext } from '../../../context'
import { FAQSection } from '../../../molecules/fAQSection/FAQSection'
import { FaqAccordion, FaqQuestion } from '../../../organisms/faqAccordion/FaqAccordion'
import { BodyTextSize, DisplaySize, TitleSize } from '../../../styles/typeStyles'
import { openUrl } from '../../../utils/expo-linking'
import { ProductTypeContext } from '../../../utils/productType/productTypeContext'
import { ThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'

const CUSTOM_COST_MESSAGE_CUSTOMERS: string[] = [CustomerName.STARBUCKS]

const CtaCopyContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingTop: '124px',
  ...(theme.breakpoints.isMinWidthTablet && { justifyContent: 'center', alignItems: 'center' }),
}))

const CtaBodyText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
  marginBottom: theme.spacing['48px'],
  ...(theme.breakpoints.isMinWidthTablet && { textAlign: 'center', maxWidth: '781px' }),
}))

const FAQsCollapsibleContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  ...(theme.breakpoints.isMobileSized && { flexDirection: 'column', paddingBottom: theme.spacing['32px'] }),
  ...(theme.breakpoints.isMinWidthTablet && {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingBottom: '200px',
    width: '100%',
  }),
}))

const DesktopSizedCreateAccountContainer = styled.ImageBackground<{ theme: ThemeType; backgroundHeight?: number }>(
  ({ theme, backgroundHeight }) => ({
    alignItems: 'center',
    width: '100%',
    ...(backgroundHeight && { height: backgroundHeight }),
    ...(theme.breakpoints.isMinWidthDesktop && {
      paddingTop: theme.breakpoints.isMinWidthDesktopL ? theme.spacing['56px'] : theme.spacing['32px'],
    }),
  }),
)

const CTAButton = styled(PrimaryButton)({
  minWidth: '148px',
})

const BottomCTAButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
  ...(!theme.breakpoints.isMinWidthDesktop && {
    paddingBottom: '124px',
  }),
}))

const DesktopSizedCtaAndCollapsibleFAQsContainer = styled.ImageBackground({
  width: '100%',
  paddingLeft: '124px',
  paddingRight: '124px',
})

export interface CustomerCostProperties {
  isCustomerEAPExclusive: boolean
  isCustomerHPIExclusive: boolean
  supportsDependents: boolean
  customerName: string
  customerDisplayName: string
  numberOfSessions?: string
  numberOfSessionsByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
}

export interface PathwaysFAQSectionProps {
  membershipType: PROGRAM_MEMBERSHIP_TYPES
  beforeLaunch: boolean
  hasMembershipInCustomer: boolean
  customerCostProperties: CustomerCostProperties
  isCostMessagingEnabled: boolean
  isAutoEnrollmentPathways?: boolean
  startDate?: string
  rerouteFromMicrosite?: () => void
  onReachOutToCareNavTeamLinkPress?: () => void
  onQuestionPress?: (title: string) => void
  onNotifyMeOnLaunchPress?: () => void
}

export const PathwaysFAQSection: FunctionComponent<PathwaysFAQSectionProps> = ({
  membershipType,
  beforeLaunch,
  hasMembershipInCustomer,
  startDate,
  customerCostProperties,
  isCostMessagingEnabled,
  isAutoEnrollmentPathways = false,
  rerouteFromMicrosite = noop,
  onReachOutToCareNavTeamLinkPress,
  onQuestionPress,
  onNotifyMeOnLaunchPress = noop,
}) => {
  const { formatDate, formatMessage } = useIntl()
  const { activeLanguage } = useLyraIntl()
  const { colors, breakpoints, spacing, isAuroraTheme } = useTheme() as ThemeType
  const { forceAdultFlowsToHPI, isCustomerDynamicSessionLimitEnabled, isHideCostAndSessionLimitEnabled } =
    useContext(AppContext)
  const { isLyraSelect } = useContext(ProductTypeContext)

  const desktopSizedBackgroundFooterAspectRatio = 3016 / 599

  // We will explicitly set background height ONLY for the footer portion of the larger image (it's more intricate) so it's aspect ratio can be preserved.
  // The smaller background image's height is fine to be auto set as it does not have any noticeable distortion to the image when stretched.
  const desktopSizedBackgroundHeightFooter = Math.round(
    Dimensions.get('window').width * (1 / desktopSizedBackgroundFooterAspectRatio),
  )

  const backgroundWFMHContainerProps = {
    source: breakpoints.isMinWidthDesktop ? SoftSunriseWithDogBGBody : SoftSunriseMobileBG,
    resizeMode: 'stretch',
    testID: breakpoints.isMinWidthDesktop
      ? tID('FAQSection-pathwaysWebBackground')
      : tID('FAQSection-pathwaysMobileBackground'),
    width: '100%',
  }

  const getAccordionStyle = (hasRightMargin: boolean) => ({
    ...(breakpoints.isMinWidthTablet && {
      flex: 1,
      height: '100%',
      marginRight: hasRightMargin && breakpoints.isMinWidthLaptop ? spacing['64px'] : spacing['32px'],
      maxWidth: '598px',
    }),
    ...(breakpoints.isMobileSized && { marginBottom: spacing['48px'] }),
  })

  const backgroundLyraSelectContainerProps = {
    ...backgroundWFMHContainerProps,
    source: breakpoints.isMinWidthDesktopL
      ? LyraSelectFooterDesktopPlus
      : breakpoints.isMinWidthDesktop
      ? LyraSelectFooterDesktop
      : breakpoints.isMinWidthLaptop
      ? LyraSelectFooterLaptop
      : breakpoints.isMinWidthTablet
      ? LyraSelectFooterTablet
      : breakpoints.isMinWidthMobileL
      ? LyraSelectFooterMobilePlus
      : LyraSelectFooterMobile,
  }

  const backgroundContainerProps = isLyraSelect ? backgroundLyraSelectContainerProps : backgroundWFMHContainerProps

  const {
    isCustomerEAPExclusive,
    isCustomerHPIExclusive,
    supportsDependents,
    customerName,
    customerDisplayName,
    displayVisitsPerIssuePerYear,
    numberOfSessionsByProgram,
    displayVisitsPerIssuePerYearByProgram,
  } = customerCostProperties

  const numberOfSessions = computeRemainderSessions(
    parseInt(customerCostProperties.numberOfSessions ?? '0'),
    numberOfSessionsByProgram,
  )

  // Determine which programs have per-issue-per-year limits for FAQ
  const therapyPerIssuePerYear = displayVisitsPerIssuePerYearByProgram?.therapy
  const coachingPerIssuePerYear = displayVisitsPerIssuePerYearByProgram?.coaching
  const hasTherapyPerIssuePerYear = !!therapyPerIssuePerYear
  const hasCoachingPerIssuePerYear = !!coachingPerIssuePerYear

  const faqEmployeeMessages = beforeLaunch
    ? { ...FAQ_EMPLOYEE_MESSAGES, ...FAQ_EMPLOYEE_MESSAGES_BEFORE_LAUNCH }
    : FAQ_EMPLOYEE_MESSAGES

  const faqMemberMessages = beforeLaunch
    ? { ...FAQ_MEMBER_MESSAGES, ...FAQ_MEMBER_MESSAGES_BEFORE_LAUNCH }
    : FAQ_MEMBER_MESSAGES

  const getEmployeeCostMessage = () => {
    if (isCustomerDynamicSessionLimitEnabled) {
      return FAQ_EMPLOYEE_COST_MESSAGES.GENERIC_DYNAMIC_SESSION_LIMIT
    }

    if (displayVisitsPerIssuePerYear) {
      return supportsDependents
        ? FAQ_EMPLOYEE_COST_MESSAGES.PER_ISSUE_PER_YEAR_WITH_DEPENDENTS
        : FAQ_EMPLOYEE_COST_MESSAGES.PER_ISSUE_PER_YEAR_WITHOUT_DEPENDENTS
    }

    if (CUSTOM_COST_MESSAGE_CUSTOMERS.includes(customerName)) {
      return supportsDependents
        ? FAQ_EMPLOYEE_COST_MESSAGES.CUSTOM_CUSTOMER_WITH_DEPENDENTS
        : FAQ_EMPLOYEE_COST_MESSAGES.CUSTOM_CUSTOMER_WITHOUT_DEPENDENTS
    }

    if (isCustomerEAPExclusive) {
      return supportsDependents
        ? FAQ_EMPLOYEE_COST_MESSAGES.EAP_ONLY_WITH_DEPENDENTS
        : FAQ_EMPLOYEE_COST_MESSAGES.EAP_ONLY_WITHOUT_DEPENDENTS
    }

    if (isCustomerHPIExclusive) {
      return supportsDependents
        ? FAQ_EMPLOYEE_COST_MESSAGES.HPI_ONLY_WITH_DEPENDENTS
        : FAQ_EMPLOYEE_COST_MESSAGES.HPI_ONLY_WITHOUT_DEPENDENTS
    }

    if (forceAdultFlowsToHPI) {
      return FAQ_EMPLOYEE_COST_MESSAGES.ADULT_HPI_ONLY_WITH_CHILD_EAP_HPI
    }

    return supportsDependents
      ? FAQ_EMPLOYEE_COST_MESSAGES.EAP_HPI_WITH_DEPENDENTS
      : FAQ_EMPLOYEE_COST_MESSAGES.EAP_HPI_WITHOUT_DEPENDENTS
  }

  const FAQ_ANSWERS = {
    IS_THE_INFORMATION_I_SHARE_CONFIDENTIAL: {
      [PROGRAM_MEMBERSHIP_TYPES.EMPLOYEE]: formatMessage(faqEmployeeMessages.IS_THE_INFORMATION_I_SHARE_CONFIDENTIAL, {
        link1: (text: string) => (
          <Link
            text={text}
            onPress={() => {
              openUrl(getLegalNoticeLink(activeLanguage, 'PRIVACY_POLICY'))
            }}
            underline
          />
        ),
        link2: (text: string) => (
          <Link
            text={text}
            onPress={() => {
              openUrl(getLegalNoticeLink(activeLanguage, 'HIPAA_NOTICE'))
            }}
            underline
          />
        ),
      }),
      [PROGRAM_MEMBERSHIP_TYPES.MEMBER]: formatMessage(faqMemberMessages.IS_THE_INFORMATION_I_SHARE_CONFIDENTIAL, {
        link1: (text: string) => (
          <Link
            text={text}
            onPress={() => {
              openUrl(getLegalNoticeLink(activeLanguage, 'PRIVACY_POLICY'))
            }}
            underline
          />
        ),
        link2: (text: string) => (
          <Link
            text={text}
            onPress={() => {
              openUrl(getLegalNoticeLink(activeLanguage, 'HIPAA_NOTICE'))
            }}
            underline
          />
        ),
      }),
    },
    HOW_MUCH_DOES_LYRA_COST: {
      [PROGRAM_MEMBERSHIP_TYPES.EMPLOYEE]:
        isCostMessagingEnabled && !isHideCostAndSessionLimitEnabled
          ? formatMessage(getEmployeeCostMessage(), {
              customerName: customerDisplayName,
              sessionCount: (displayVisitsPerIssuePerYear ?? numberOfSessions) || 0,
              programLevelSessionLimitEnabled:
                !!numberOfSessionsByProgram?.therapy && !!numberOfSessionsByProgram?.coaching,
              hasTherapyPerIssuePerYear,
              hasCoachingPerIssuePerYear,
              therapyPerIssuePerYear,
              coachingPerIssuePerYear,
              therapySessionCount: numberOfSessionsByProgram?.therapy,
              coachingSessionCount: numberOfSessionsByProgram?.coaching,
              isUnlimitedCoaching: isUnlimitedSessionLimit(numberOfSessionsByProgram?.coaching ?? 0),
              isAutoEnrollmentPathways,
              bold: (chunks: string) => (
                <BodyText bold size={BodyTextSize.DEFAULT} color={colors.textPrimary} text={chunks} />
              ),
            })
          : formatMessage(faqEmployeeMessages.HOW_MUCH_DOES_LYRA_COST),
      [PROGRAM_MEMBERSHIP_TYPES.MEMBER]: formatMessage(faqMemberMessages.HOW_MUCH_DOES_LYRA_COST),
    },
    HOW_MUCH_DOES_LYRA_COST_LYRA_SELECT: formatMessage(FAQ_LYRA_SELECT_MESSAGES.HOW_MUCH_DOES_LYRA_COST),
    WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER: {
      [PROGRAM_MEMBERSHIP_TYPES.EMPLOYEE]: formatMessage(faqEmployeeMessages.WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER),
      [PROGRAM_MEMBERSHIP_TYPES.MEMBER]: formatMessage(faqMemberMessages.WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER),
    },
    WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER_LYRA_SELECT: formatMessage(
      FAQ_LYRA_SELECT_MESSAGES.WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER,
    ),
    HOW_DO_I_GET_STARTED: {
      [PROGRAM_MEMBERSHIP_TYPES.EMPLOYEE]: formatMessage(faqEmployeeMessages.HOW_DO_I_GET_STARTED, {
        isAutoEnrollmentPathways,
      }),
      [PROGRAM_MEMBERSHIP_TYPES.MEMBER]: formatMessage(faqMemberMessages.HOW_DO_I_GET_STARTED),
    },
    HOW_DO_I_GET_STARTED_LYRA_SELECT: formatMessage(FAQ_LYRA_SELECT_MESSAGES.HOW_DO_I_GET_STARTED),
    HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED: {
      [PROGRAM_MEMBERSHIP_TYPES.EMPLOYEE]: formatMessage(faqEmployeeMessages.HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED),
      [PROGRAM_MEMBERSHIP_TYPES.MEMBER]: formatMessage(faqMemberMessages.HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED),
    },
    HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED_LYRA_SELECT: formatMessage(
      FAQ_LYRA_SELECT_MESSAGES.HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED,
    ),
    HOW_WILL_I_MEET_WITH_MY_PROVIDER: {
      [PROGRAM_MEMBERSHIP_TYPES.EMPLOYEE]: (
        <>
          <BodyText>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <FormattedMessage {...faqEmployeeMessages.HOW_WILL_I_MEET_WITH_MY_PROVIDER_FIRST_PARAGRAPH} />{' '}
          </BodyText>
          <BodyText>
            <FormattedMessage
              {...faqEmployeeMessages.HOW_WILL_I_MEET_WITH_MY_PROVIDER_SECOND_PARAGRAPH}
              values={{
                link: (text: string) => (
                  <Link
                    text={text}
                    onPress={() => {
                      onReachOutToCareNavTeamLinkPress && onReachOutToCareNavTeamLinkPress()
                    }}
                    underline
                  />
                ),
              }}
            />
          </BodyText>
        </>
      ),
      [PROGRAM_MEMBERSHIP_TYPES.MEMBER]: formatMessage(faqMemberMessages.HOW_WILL_I_MEET_WITH_MY_PROVIDER),
    },
    DOES_LYRA_HAVE_PROVIDERS_WHO_SHARE_MY_SOCIAL_CULTURAL_IDENTITIES: formatMessage(
      FAQ_MESSAGES_DEFAULT.DOES_LYRA_HAVE_PROVIDERS_WHO_SHARE_MY_SOCIAL_CULTURAL_IDENTITIES,
    ),
    DOES_LYRA_HAVE_PROVIDERS_THAT_SPECIALIZE_IN_MY_SPECIFIC_NEEDS: formatMessage(
      FAQ_MESSAGES_DEFAULT.DOES_LYRA_HAVE_PROVIDERS_THAT_SPECIALIZE_IN_MY_SPECIFIC_NEEDS,
    ),
    WHO_ARE_LYRAS_CARE_PROVIDERS: formatMessage(FAQ_LYRA_SELECT_MESSAGES.WHO_ARE_LYRAS_CARE_PROVIDERS),
  }

  const aboutLyraFAQsDefault: FaqQuestion[] = [
    {
      question: FAQ_QUESTIONS.HOW_IS_THE_INFORMATION_I_SHARE_CONFIDENTIAL,
      answer: FAQ_ANSWERS.IS_THE_INFORMATION_I_SHARE_CONFIDENTIAL[membershipType],
    },
    {
      question: FAQ_QUESTIONS.HOW_MUCH_DOES_LYRA_COST,
      answer: isLyraSelect
        ? FAQ_ANSWERS.HOW_MUCH_DOES_LYRA_COST_LYRA_SELECT
        : FAQ_ANSWERS.HOW_MUCH_DOES_LYRA_COST[membershipType],
    },
    {
      question: FAQ_QUESTIONS.WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER,
      answer: isLyraSelect
        ? FAQ_ANSWERS.WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER_LYRA_SELECT
        : FAQ_ANSWERS.WHAT_TYPE_OF_SERVICES_DOES_LYRA_OFFER[membershipType],
    },
    ...(isLyraSelect
      ? [
          {
            question: FAQ_QUESTIONS.WHO_ARE_LYRAS_CARE_PROVIDERS,
            answer: FAQ_ANSWERS.WHO_ARE_LYRAS_CARE_PROVIDERS,
          },
        ]
      : [
          {
            question: FAQ_QUESTIONS.DOES_LYRA_HAVE_PROVIDERS_WHO_SHARE_MY_SOCIAL_CULTURAL_IDENTITIES,
            answer: FAQ_ANSWERS.DOES_LYRA_HAVE_PROVIDERS_WHO_SHARE_MY_SOCIAL_CULTURAL_IDENTITIES,
          },
          {
            question: FAQ_QUESTIONS.DOES_LYRA_HAVE_PROVIDERS_THAT_SPECIALIZE_IN_MY_SPECIFIC_NEEDS,
            answer: FAQ_ANSWERS.DOES_LYRA_HAVE_PROVIDERS_THAT_SPECIALIZE_IN_MY_SPECIFIC_NEEDS,
          },
        ]),
  ]

  const howDoesItWorkFAQsDefault = [
    {
      question: FAQ_QUESTIONS.HOW_DO_I_GET_STARTED,
      answer: isLyraSelect
        ? FAQ_ANSWERS.HOW_DO_I_GET_STARTED_LYRA_SELECT
        : FAQ_ANSWERS.HOW_DO_I_GET_STARTED[membershipType],
    },
    {
      question: FAQ_QUESTIONS.HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED,
      answer: isLyraSelect
        ? FAQ_ANSWERS.HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED_LYRA_SELECT
        : FAQ_ANSWERS.HOW_LONG_DOES_IT_TAKE_TO_GET_STARTED[membershipType],
      hideQuestion: isAutoEnrollmentPathways,
    },
    ...(isLyraSelect
      ? []
      : [
          {
            question: FAQ_QUESTIONS.HOW_WILL_I_MEET_WITH_MY_PROVIDER,
            answer: FAQ_ANSWERS.HOW_WILL_I_MEET_WITH_MY_PROVIDER[membershipType],
          },
        ]),
  ]

  const FAQs = [
    {
      title: (
        <FormattedMessage
          defaultMessage='About Lyra'
          description='Microsite FAQ section header 1, questions about the Lyra Health benefit'
        />
      ),
      questions: aboutLyraFAQsDefault,
    },
    {
      title: (
        <FormattedMessage
          defaultMessage='How does it work?'
          description='Microsite FAQ section header 2, questions about getting care with Lyra Health'
        />
      ),
      questions: howDoesItWorkFAQsDefault,
    },
  ]

  const stillHaveQuestionsText = (
    <FormattedMessage
      defaultMessage='Still have questions?'
      description='Header text, about how to get answers to employer-specific questions, and about contacting the care navigator team'
    />
  )
  const ctaHeader = breakpoints.isMobileSized ? (
    <Title testID={tID('PathwaysFAQSection-ctaHeader')} size={TitleSize.LARGE} text={stillHaveQuestionsText} />
  ) : (
    <Display testID={tID('PathwaysFAQSection-ctaHeader')} size={DisplaySize.SMALL} text={stillHaveQuestionsText} />
  )

  let ctaSubHeader = (
    <FormattedMessage
      defaultMessage='Here are just a few of our frequently asked questions. You can find more information specific to your employer’s benefits plan by signing into your {isLyraSelect, select, true {free } other {}}Lyra account.'
      description='The client can get employer specific details by signing into their account, under the section (Still have questions?)'
      values={{ isLyraSelect }}
    />
  )
  if (hasMembershipInCustomer) {
    ctaSubHeader = (
      <FormattedMessage
        defaultMessage='Here are just a few of our frequently asked questions. For more specific details about your personalized Lyra experience, sign into your account.'
        description='The client can get employer specific details by signing into their account, under the section (Still have questions?)'
      />
    )
  } else if (isAutoEnrollmentPathways) {
    ctaSubHeader = (
      <FormattedMessage
        defaultMessage='Here are just a few of our frequently asked questions.'
        description='The client can get employer specific details by signing into their account, under the section (Still have questions?)'
      />
    )
  }

  const ctaSection = beforeLaunch ? (
    <View>
      <CtaCopyContainer>
        {ctaHeader}
        <CtaBodyText>
          <FormattedMessage
            defaultMessage='Get answers to common questions before the Lyra benefit launches then come back {launchDate} to register and learn more.'
            description='The client can get employer specific details once their benefit launches'
            values={{
              launchDate: formatDate(startDate, {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              }),
            }}
          />
        </CtaBodyText>
      </CtaCopyContainer>
    </View>
  ) : (
    <View>
      <CtaCopyContainer>
        {ctaHeader}
        <CtaBodyText testID={tID('PathwaysFAQSection-ctaSubHeader')}>{ctaSubHeader}</CtaBodyText>
      </CtaCopyContainer>
    </View>
  )

  const pathwaysFaqsCollapsibleContent = (
    <FAQsCollapsibleContentContainer>
      {FAQs.map(({ title, questions }, index) => (
        <FaqAccordion
          title={title}
          questions={questions}
          key={index}
          style={getAccordionStyle(FAQs.length - 1 !== index)}
          testId={`PathwaysFAQSection-col-${index}`}
          onQuestionPress={onQuestionPress}
          headingLevel='3'
        />
      ))}
    </FAQsCollapsibleContentContainer>
  )

  const BottomCTAWrapper =
    breakpoints.isMinWidthDesktop && !isAuroraTheme ? DesktopSizedCreateAccountContainer : React.Fragment
  const bottomCTAContainerWrapperProps =
    breakpoints.isMinWidthDesktop && !isAuroraTheme
      ? {
          resizeMode: 'stretch',
          source: isLyraSelect ? undefined : SoftSunriseWithDogBGFooter,
          backgroundHeight: breakpoints.isMinWidthDesktop ? desktopSizedBackgroundHeightFooter : undefined,
          testID: 'BottomCTAContainer',
        }
      : {}

  const readyToGetStartedText = beforeLaunch ? (
    <FormattedMessage
      defaultMessage='Launching {launchDate}'
      description='Header above the notify me on launch button'
      values={{
        launchDate: formatDate(startDate, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        }),
      }}
    />
  ) : (
    <FormattedMessage defaultMessage='Ready to get started?' description='Header above the create account button' />
  )

  const bottomCTASectionHeader = breakpoints.isMobileSized ? (
    <Title
      size={TitleSize.LARGE}
      text={readyToGetStartedText}
      color={isAuroraTheme ? colors.textSecondary : undefined}
    />
  ) : (
    <Display
      size={DisplaySize.MEDIUM}
      text={readyToGetStartedText}
      color={isAuroraTheme ? colors.textSecondary : undefined}
    />
  )

  const bottomCTASection = isAutoEnrollmentPathways ? (
    <></>
  ) : (
    <BottomCTAWrapper {...bottomCTAContainerWrapperProps}>
      <View testID={tID('FAQSection-bottomCTASectionHeader')}>{bottomCTASectionHeader}</View>
      <BottomCTAButtonContainer>
        <CTAButton
          text={
            beforeLaunch ? (
              <FormattedMessage
                defaultMessage='Notify me on launch'
                description='button text for notifying on launch'
              />
            ) : isLyraSelect ? (
              <FormattedMessage
                defaultMessage='Create your account'
                description='button text for creating a new account'
              />
            ) : (
              <FormattedMessage defaultMessage='Sign up' description='button text for creating a new account' />
            )
          }
          onPress={() => {
            if (beforeLaunch) {
              onNotifyMeOnLaunchPress()
            } else {
              rerouteFromMicrosite()
            }
          }}
          testID={tID(`FAQSection-${beforeLaunch ? 'notifyMeOnLaunchButton' : 'createAccountButton'}`)}
        />
      </BottomCTAButtonContainer>
    </BottomCTAWrapper>
  )

  const CtaAndFAQsWrapper =
    breakpoints.isMinWidthDesktop && !isAuroraTheme ? DesktopSizedCtaAndCollapsibleFAQsContainer : React.Fragment
  const ctaAndFAQsWrapperProps =
    breakpoints.isMinWidthDesktop && !isAuroraTheme
      ? {
          resizeMode: 'stretch',
          source: isLyraSelect ? undefined : SoftSunriseWithDogBGBody,
        }
      : {}

  const ctaAndFAQsSection = (
    <CtaAndFAQsWrapper {...ctaAndFAQsWrapperProps}>
      {ctaSection}
      {pathwaysFaqsCollapsibleContent}
    </CtaAndFAQsWrapper>
  )
  const auroraBackgroundStyle: ViewStyle = {
    backgroundColor: colors.backgroundSecondary,
    paddingBottom: breakpoints.isMinWidthDesktop ? 124 : undefined,
  }
  const auroraOverlayStyle: ViewStyle = {
    backgroundColor: colors.components.pathwaysFAQSection.overlay.background,
  }

  return (
    <FAQSection
      ctaAndCollapsibleFAQsSection={ctaAndFAQsSection}
      bottomCTA={bottomCTASection}
      backgroundContainerProps={isAuroraTheme ? { style: auroraBackgroundStyle } : backgroundContainerProps}
      hasBackgroundImage={!isAuroraTheme}
      overlayStyle={isAuroraTheme ? auroraOverlayStyle : undefined}
    />
  )
}
